/**
 * Configurable **appearance** for the campaign-analytics charts (Req 6).
 *
 * The campaign-analytics charts are drawn from app-computed data + drill state — that stays owned by
 * the code. What an admin may configure through the existing custom-tile section is only the chart's
 * *appearance*: which bar chart type to use and the cosmetic settings (colours, legend, borders,
 * padding, …). This module resolves that: given a stable slug, it looks for a persisted `CustomTile`
 * (in the "Campaign Analytics" category, named for the slug) and, if found, merges its cosmetic
 * settings over the code-supplied fallback. The data, the drill, and the load-bearing shaping settings
 * (`labelField`/`keys`/`groupMode`/`layout`/axis ticks/value format) always come from the app.
 *
 * Fully backward-compatible: no category or no matching tile ⇒ the built-in fallback is returned
 * unchanged, so the charts render exactly as before until someone authors an override.
 */
import * as React from 'react';
import type { CustomTile, CustomTileCategory } from '../types/dashboard';
import type { AnalyticsChartType } from './campaignAnalytics';
import { useFetchCustomTiles, useFetchCustomTileCategories } from '../hooks/toolbox-hooks';

/** The custom-tile category an admin creates to hold campaign-analytics appearance overrides. */
export const CAMPAIGN_ANALYTICS_CATEGORY = 'Campaign Analytics';

/** Stable slugs (used as the persisted `CustomTile.name`) for each configurable campaign chart. */
export const CampaignChartSlug = {
  deliveryProgression: 'campaign.deliveryProgression',
} as const;
export type CampaignChartSlug = (typeof CampaignChartSlug)[keyof typeof CampaignChartSlug];

/** The resolved appearance the app feeds into `ChartCard`. */
export interface TileAppearance {
  chartType: AnalyticsChartType;
  settings: Record<string, any>;
}

/** Chart types an appearance override may switch to — bar variants only (the data is a bar/stack). */
const ALLOWED_CHART_TYPES: ReadonlySet<string> = new Set<AnalyticsChartType>([
  'horizontal_bar_chart',
  'vertical_bar_chart',
]);

/**
 * Settings that shape *what* the chart plots — always taken from the app fallback, never from a
 * persisted tile, so an override can't break the data contract (it would if it changed `keys` order,
 * the label field, the stack mode, the layout, or the integer axis ticks).
 */
const PROTECTED_SETTING_KEYS: ReadonlySet<string> = new Set([
  'labelField',
  'valueField',
  'keys',
  'groupMode',
  'layout',
  'axisLeftTickValues',
  'axisBottomTickValues',
  'valueFormat',
]);

/**
 * Resolve a chart's appearance: return the code `fallback` unless a persisted `CustomTile` overrides
 * it, in which case take an allowlisted bar `chart_type` and the tile's *cosmetic* settings layered
 * over the fallback (protected shaping keys stay from the fallback).
 */
export function resolveTileAppearance(
  slug: CampaignChartSlug,
  tiles: CustomTile[] | undefined,
  categories: CustomTileCategory[] | undefined,
  fallback: TileAppearance,
): TileAppearance {
  const category = (categories ?? []).find((c) => c.name === CAMPAIGN_ANALYTICS_CATEGORY);
  if (!category) return fallback;
  const tile = (tiles ?? []).find((t) => t.category === category.id && t.name === slug);
  if (!tile) return fallback;

  const chartType = ALLOWED_CHART_TYPES.has(tile.chart_type)
    ? (tile.chart_type as AnalyticsChartType)
    : fallback.chartType;

  const settings: Record<string, any> = { ...fallback.settings };
  const persisted = (tile.settings ?? {}) as Record<string, any>;
  for (const key of Object.keys(persisted)) {
    if (!PROTECTED_SETTING_KEYS.has(key)) settings[key] = persisted[key];
  }

  return { chartType, settings };
}

/**
 * React hook wrapping {@link resolveTileAppearance}: fetches the custom tiles + categories and returns
 * the resolved appearance for `slug`, falling back to `fallback` while loading or when unconfigured.
 * `fallback` is memoised by the caller (it carries the app-computed shaping settings).
 */
export function useCampaignTileAppearance(
  slug: CampaignChartSlug,
  fallback: TileAppearance,
): TileAppearance {
  const { data: tiles } = useFetchCustomTiles();
  const { data: categories } = useFetchCustomTileCategories();
  return React.useMemo(
    () => resolveTileAppearance(slug, tiles as CustomTile[] | undefined, categories, fallback),
    [slug, tiles, categories, fallback],
  );
}
